// EffectiveSLP — daily-retry-count state for the kid-facing practice
// player (issue #353 / #385).
//
// Extracted from ChildPracticePlayer.tsx as a pure module so:
//   - the in-memory fallback (`inMemoryRetryCounts` Map) and its
//     prune helper can be unit-tested under `tsx` without dragging
//     in React/JSX,
//   - the storage-key shape is documented in one place (the
//     `retryCountStorageKey` contract is load-bearing for the
//     daily-cap + persistence-failure paths).
//
// Why this exists (issue #353):
//   `localStorage.setItem` can throw (quota exceeded, private-mode
//   write blocks, etc.) — and if it does, the persisted value
//   never moves and a fresh `readRetryCount` keeps returning the
//   stale / zero value on every render. That defeats the daily
//   retry cap the whole `MAX_DAILY_RETRIES_PER_ACTIVITY` mechanism
//   exists to enforce. To keep the cap authoritative even when
//   persistence fails, every `incrementRetryCount` call ALSO bumps
//   the in-memory map, and `readRetryCount` consults it as a
//   fallback when localStorage comes back empty. Module-scoped so
//   the counter survives across renders within a single page
//   lifetime but resets on reload — same trust model as the
//   localStorage value itself.
//
// Why pruning exists (issue #385):
//   Keys embed today's date (`retryCountStorageKey(tokenId, hash,
//   todayKey(), itemId)`), so entries from prior days are never
//   queried again after the date rolls over. Without pruning the
//   map would grow by one entry per (child, activity-item, day)
//   combination incremented within a single page lifetime.
//   `pruneStaleRetryEntries(today)` walks the map on every touch
//   and drops entries whose date segment does not match today's
//   `YYYY-MM-DD`. Cost is O(N) over a map that in practice holds
//   tens of entries across a session, and the entries themselves
//   are tiny string→number — pruning is negligible relative to
//   the rest of the retry-cap machinery.

/** localStorage-shaped key: `practice:<tokenId>:retries:<hash>:<date>:<itemId>`.
 *  The date segment sits at index 4 of the `:`-split key — the prune
 *  helper relies on this layout. */
export function retryCountStorageKey(
  tokenId: string,
  hash: string,
  date: string,
  itemId: string,
): string {
  return `practice:${tokenId}:retries:${hash}:${date}:${itemId}`;
}

/** Local-time YYYY-MM-DD so the daily retry cap resets at the user's
 *  local midnight, not UTC midnight. See issue #352. */
export function todayKey(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Module-scoped in-memory mirror of the persisted daily retry
 * counts. Survives renders within a single page lifetime; resets
 * on page reload — same trust model as the localStorage value
 * itself.
 *
 * Exported only so the prune helper (and tests) can reach it
 * without going through the persistence-touching wrapper.
 */
export const inMemoryRetryCounts = new Map<string, number>();

/**
 * Drop entries from `inMemoryRetryCounts` whose embedded date
 * (segment index 4 of the storage key, between `${hash}` and
 * `${itemId}`) is not `today`. Safe to call on every
 * `readRetryCount` / `incrementRetryCount` invocation.
 *
 * Foreign keys (length !== 6 after `:` split) are left alone —
 * pruning is best-effort hygiene, not a sanitizer.
 */
export function pruneStaleRetryEntries(today: string): void {
  for (const key of Array.from(inMemoryRetryCounts.keys())) {
    const parts = key.split(':');
    if (parts.length !== 6) continue;
    if (parts[4] !== today) {
      inMemoryRetryCounts.delete(key);
    }
  }
}

/**
 * Reset the in-memory map. Tests use this between cases; not
 * called from production code.
 */
export function resetInMemoryRetryCounts(): void {
  inMemoryRetryCounts.clear();
}

/**
 * Read the persisted retry count for (tokenId, hash, itemId) on
 * `today`, falling back to the in-memory mirror when
 * localStorage is missing or throws. Returns the max of the two
 * so a successful increment-then-persist-then-render cycle
 * never regresses.
 */
export function readRetryCount(
  tokenId: string,
  hash: string,
  itemId: string,
  today: string = todayKey(),
): number {
  pruneStaleRetryEntries(today);
  const key = retryCountStorageKey(tokenId, hash, today, itemId);
  let persisted = 0;
  if (typeof localStorage !== 'undefined') {
    try {
      const value = Number(localStorage.getItem(key));
      if (Number.isFinite(value) && value >= 0) persisted = value;
    } catch {
      // localStorage unavailable — fall through to the in-memory
      // fallback below so the cap is still enforced.
    }
  }
  const inMemory = inMemoryRetryCounts.get(key) ?? 0;
  return Math.max(persisted, inMemory);
}

/**
 * Increment the retry count for (tokenId, hash, itemId) on
 * `today`, bumping both the in-memory mirror and localStorage.
 * Returns the new count.
 *
 * If localStorage throws (quota / private-mode write block) the
 * in-memory map still records the increment for the rest of this
 * page lifetime, so the cap is still authoritative.
 */
export function incrementRetryCount(
  tokenId: string,
  hash: string,
  itemId: string,
  today: string = todayKey(),
): number {
  pruneStaleRetryEntries(today);
  const key = retryCountStorageKey(tokenId, hash, today, itemId);
  const next = readRetryCount(tokenId, hash, itemId, today) + 1;
  inMemoryRetryCounts.set(key, next);
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.setItem(key, String(next));
    } catch {
      // localStorage quota / write block — the in-memory map above
      // still enforces the cap for the rest of this page lifetime.
    }
  }
  return next;
}